import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, CircleNotch, Copy, LinkSimple, ShareNetwork, Trash, Image as ImageIcon } from "@phosphor-icons/react";
import api, { API, formatApiErrorDetail } from "@/lib/api";

const fmtPrice = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "-";
};

function MobileCartItem({ item, coefficient, onRemove, onCartonQtyChange, removing }) {
  const [imgError, setImgError] = useState(false);
  const [qtyDrafts, setQtyDrafts] = useState({});

  const commitQty = (variantId) => {
    const raw = qtyDrafts[variantId];
    if (raw === undefined) return;
    const parsed = Math.max(0, parseInt(raw, 10) || 0);
    setQtyDrafts((prev) => ({ ...prev, [variantId]: undefined }));
    onCartonQtyChange(item.id, variantId, parsed);
  };

  return (
    <div className="rounded-sm border border-zinc-300 bg-white p-3" data-testid={`mobile-cart-item-${item.item_no}`}>
      <div className="flex gap-3">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-zinc-200 bg-zinc-50">
          {!imgError ? (
            <img
              src={`${API}/cart/items/${item.id}/photo`}
              alt={item.item_no}
              className="h-full w-full object-contain"
              onError={() => setImgError(true)}
            />
          ) : (
            <ImageIcon size={20} className="text-zinc-300" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-bold text-zinc-900">{item.item_no}</p>
              <p className="mt-0.5 line-clamp-2 text-xs text-zinc-600">{item.description || "-"}</p>
            </div>
            <button
              type="button"
              onClick={() => onRemove(item.id)}
              disabled={removing}
              className="shrink-0 rounded-sm border border-red-200 p-2 text-red-500 hover:bg-red-50 disabled:opacity-50"
              data-testid={`mobile-cart-remove-${item.item_no}`}
            >
              {removing ? <CircleNotch size={14} className="animate-spin" /> : <Trash size={14} />}
            </button>
          </div>
        </div>
      </div>

      {(item.variants || []).length > 0 && (
        <div className="mt-2 overflow-hidden rounded-sm border border-zinc-200">
          <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 bg-zinc-100 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.05em] text-zinc-500">
            <span>Size</span>
            <span className="text-right">Price</span>
            <span className="text-right">Pcs/CTN</span>
            <span className="text-right">Carton Qty</span>
          </div>
          {item.variants.map((v) => (
            <div
              key={v.variant_record_id}
              className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 border-t border-zinc-100 px-2 py-1.5 text-xs text-zinc-800"
            >
              <span className="truncate">{v.size || "-"}</span>
              <span className="text-right font-mono-data text-blue-700">{fmtPrice(v.final_price ?? v.price)}</span>
              <span className="text-right">{v.packing_qty || "-"}</span>
              <Input
                type="number"
                min="0"
                inputMode="numeric"
                value={qtyDrafts[v.variant_record_id] ?? (v.carton_qty || "")}
                placeholder="0"
                onChange={(e) => setQtyDrafts((prev) => ({ ...prev, [v.variant_record_id]: e.target.value }))}
                onBlur={() => commitQty(v.variant_record_id)}
                className="h-8 w-16 rounded-sm border-zinc-300 text-right text-xs"
                data-testid={`mobile-cart-carton-qty-${item.item_no}-${v.variant_record_id}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MobileCart() {
  const navigate = useNavigate();
  const { toast } = useToast();
  useAuth();

  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch (e) {
      toast({
        title: "Failed to load cart",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleRemove = async (itemId) => {
    setRemovingId(itemId);
    try {
      await api.delete(`/cart/items/${itemId}`);
      setCart((prev) => ({ ...prev, items: prev.items.filter((i) => i.id !== itemId) }));
      toast({ title: "Removed from cart" });
    } catch (e) {
      toast({
        title: "Could not remove item",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setRemovingId(null);
    }
  };

  const handleCartonQtyChange = async (itemId, variantId, qty) => {
    setCart((prev) => ({
      ...prev,
      items: prev.items.map((i) =>
        i.id === itemId
          ? { ...i, variants: i.variants.map((v) => (v.variant_record_id === variantId ? { ...v, carton_qty: qty } : v)) }
          : i
      ),
    }));
    try {
      await api.patch(`/cart/items/${itemId}/variants/${variantId}`, { carton_qty: qty });
    } catch (e) {
      toast({
        title: "Could not update quantity",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    }
  };

  const handleShareLink = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/cart/share-link");
      setShareLink(data.link);
      setCopied(false);
      setShareOpen(true);
    } catch (e) {
      toast({
        title: "Could not create share link",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast({ title: "Link copied" });
    } catch {
      toast({ title: "Copy failed - long-press the link to copy manually", variant: "destructive" });
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: `Product List - ${cart?.customer_name || ""}`, url: shareLink });
    } catch {
      /* user cancelled */
    }
  };

  const items = cart?.items || [];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="mobile-cart-page">
      <div className="sticky top-0 z-10 border-b border-zinc-300 bg-white">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-auto rounded-sm border-zinc-300 p-2"
              onClick={() => navigate("/mobile")}
              data-testid="mobile-cart-back-button"
            >
              <ArrowLeft size={16} />
            </Button>
            <div className="min-w-0">
              <h1 className="truncate font-heading text-base font-semibold tracking-tight text-zinc-900" data-testid="mobile-cart-title">
                {cart?.customer_name || "Cart"}
              </h1>
              <p className="text-[10px] uppercase tracking-[0.05em] text-zinc-400" data-testid="mobile-cart-count">
                {items.length} product{items.length === 1 ? "" : "s"}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            className="h-9 rounded-sm bg-blue-600 px-3 text-white hover:bg-blue-700"
            onClick={handleShareLink}
            disabled={sharing || items.length === 0}
            data-testid="mobile-cart-share-link-button"
          >
            {sharing ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <LinkSimple size={16} className="mr-1.5" />}
            Share Link
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4">
        {loading ? (
          <div className="flex justify-center py-16 text-zinc-400">
            <CircleNotch size={24} className="animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-zinc-500" data-testid="mobile-cart-empty">
            Cart is empty. Add products from the product list.
          </div>
        ) : (
          items.map((item) => (
            <MobileCartItem
              key={item.id}
              item={item}
              coefficient={cart?.coefficient}
              onRemove={handleRemove}
              onCartonQtyChange={handleCartonQtyChange}
              removing={removingId === item.id}
            />
          ))
        )}
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="mobile-cart-share-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Share Link Created</DialogTitle>
            <DialogDescription>
              Anyone with this link can view the product list (valid for 30 days, no login needed).
            </DialogDescription>
          </DialogHeader>
          <div className="break-all rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700" data-testid="mobile-cart-share-link-text">
            {shareLink}
          </div>
          <div className="flex gap-2">
            <Button
              className="flex-1 rounded-sm bg-blue-600 text-white hover:bg-blue-700"
              onClick={handleCopy}
              data-testid="mobile-cart-copy-link-button"
            >
              <Copy size={16} className="mr-1.5" /> {copied ? "Copied!" : "Copy Link"}
            </Button>
            {typeof navigator !== "undefined" && navigator.share && (
              <Button
                variant="outline"
                className="flex-1 rounded-sm border-zinc-300"
                onClick={handleNativeShare}
                data-testid="mobile-cart-native-share-button"
              >
                <ShareNetwork size={16} className="mr-1.5" /> Share...
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
